
import { MILLION, THOUSAND, ZERO, NS_MIN, NS_MAX, DAY_NANOS } from './helpers/constants.js'
import { addDateTime } from './helpers/math.js'
import { balanceISODateTime } from './helpers/balance.js'
import ObjectCache from './helpers/ObjectCache.js'
import { getOption } from './helpers/utils.js'
import DateTime, { partsFromEpoch } from './DateTime.js'
import { getCanonicalTimeZoneIdentifier } from '@neumatter/intl'
import SlotMap from '@neumatter/slot-map'

class Formatter {
  static #cache = new Map()
  static #positionMap = new Map([
    ['year', 0],
    ['month', 1],
    ['day', 2],
    ['hour', 3],
    ['minute', 4],
    ['second', 5],
    ['fractionalSecond', 6]
  ])

  static positionFromType (type) {
    const pos = this.#positionMap.get(type)
    return pos === undefined ? -1 : pos
  }

  /**
   *
   * @param {string} timeZone
   * @returns {Formatter}
   */
  static from (timeZone) {
    let formatter = this.#cache.get(timeZone)

    if (formatter === undefined) {
      formatter = new Formatter(timeZone)
      this.#cache.set(timeZone, formatter)
    }

    return formatter
  }

  #intl

  constructor (timeZone) {
    this.#intl = new Intl.DateTimeFormat('en-US', {
      hour12: false,
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      era: 'short'
    })
  }

  toOffsetNanoseconds (epochNanoseconds) {
    const { year, month, day, hour, minute, second, millisecond, microsecond, nanosecond } = this.fieldsFromEpoch(epochNanoseconds)
    const utc = epochFromParts(year, month, day, hour, minute, second, millisecond, microsecond, nanosecond)
    if (utc === null) throw new RangeError('Date outside of supported range')
    return Number(utc - epochNanoseconds)
  }

  fieldsFromEpoch (epochNanoseconds) {
    const { epochMilliseconds, millisecond, microsecond, nanosecond } = partsFromEpoch(epochNanoseconds)
    const [year, month, day, hour, minute, second] = this.toLocaleParts(epochMilliseconds)
    return balanceISODateTime(year, month, day, hour, minute, second, millisecond, microsecond, nanosecond)
  }

  toLocaleParts (date) {
    if (typeof this.#intl.formatToParts === 'function') {
      const literalParts = this.#intl.formatToParts(date)
      let index = -1
      const { length } = literalParts
      const response = []

      let era = null
      while (++index < length) {
        const { type, value } = literalParts[index]

        if (type === 'era') {
          era = value
          continue
        }

        const pos = Formatter.positionFromType(type)

        if (pos !== -1) {
          response[pos] = Number(value)
        }
      }

      if (era && era === 'BC') {
        response[0] = -Math.abs(response[0]) + 1
      }

      if (response[3] === 24) {
        response[3] = 0
      }

      return response
    }

    const formatted = this.#intl.format(date).replace(/\u200E/g, '')
    const parsed = formatted.match(
      /(\d+)\/(\d+)\/(\d+) (AD|BC),? (\d+):(\d+):(\d+)/
    )

    const [, month, day, year, adOrBc, hour, minute, second] = parsed
    return [Number(adOrBc === 'BC' ? -Math.abs(year) + 1 : year), Number(month), Number(day), Number(hour === 24 ? 0 : hour), Number(minute), Number(second)]
  }
}

function disambiguatePossibleInstants (
  tz,
  possibleInstants,
  dateTime,
  disambiguation
) {
  const numInstants = possibleInstants.length

  if (numInstants === 1) return possibleInstants[0]
  if (numInstants) {
    switch (disambiguation) {
      case 'compatible':
      // fall through because 'compatible' means 'earlier' for "fall back" transitions
      case 'earlier':
        return possibleInstants[0]
      case 'later':
        return possibleInstants[numInstants - 1]
      case 'reject': {
        throw new RangeError('multiple instants found')
      }
    }
  }

  const formatter = Formatter.from(tz.id)
  const { year, month, day, hour, minute, second, millisecond, microsecond, nanosecond } = dateTime
  const utcns = epochFromParts(year, month, day, hour, minute, second, millisecond, microsecond, nanosecond)
  if (utcns === null) throw new RangeError('DateTime outside of supported range')
  const dayBefore = utcns - DAY_NANOS
  const dayAfter = utcns + DAY_NANOS
  const offsetBefore = formatter.toOffsetNanoseconds(dayBefore)
  const offsetAfter = formatter.toOffsetNanoseconds(dayAfter)
  const nanoseconds = offsetAfter - offsetBefore

  switch (disambiguation) {
    case 'earlier': {
      const calendar = dateTime.calendar

      const earlier = addDateTime(
        year,
        month,
        day,
        hour,
        minute,
        second,
        millisecond,
        microsecond,
        nanosecond,
        calendar,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        -nanoseconds,
        undefined
      )

      const earlierPlainDateTime = new DateTime(
        earlier.year,
        earlier.month,
        earlier.day,
        earlier.hour,
        earlier.minute,
        earlier.second,
        earlier.millisecond,
        earlier.microsecond,
        earlier.nanosecond,
        calendar
      )

      return tz.getPossibleEpochNanosecondsFor(earlierPlainDateTime)[0]
    }

    case 'compatible':
    // fall through because 'compatible' means 'later' for "spring forward" transitions
    case 'later': {
      const calendar = dateTime.calendar
      const later = addDateTime(
        year,
        month,
        day,
        hour,
        minute,
        second,
        millisecond,
        microsecond,
        nanosecond,
        calendar,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        nanoseconds,
        undefined
      )
      const laterPlainDateTime = new DateTime(
        later.year,
        later.month,
        later.day,
        later.hour,
        later.minute,
        later.second,
        later.millisecond,
        later.microsecond,
        later.nanosecond,
        calendar
      )

      const possible = tz.getPossibleEpochNanosecondsFor(laterPlainDateTime)
      return possible[possible.length - 1]
    }

    case 'reject': {
      throw new RangeError('no such instant found')
    }
  }
}

export default class TimeZone {
  /**
   *
   * @param {string} id
   * @returns {string | undefined}
   */
  static getCanonicalIdentifier (id) {
    if (id === 'UTC') {
      return 'UTC'
    }

    return getCanonicalTimeZoneIdentifier(id)
  }

  static hasDST (timeZone) {
    timeZone = TimeZone.from(timeZone)
    const now = DateTime.now({ timeZone })

    const twelve = now.with({ month: 12 })
    const six = now.with({ month: 6 })

    return (
      timeZone.id !== 'UTC' &&
      twelve.offsetNanoseconds !== six.offsetNanoseconds
    )
  }

  /**
   *
   * @param {string | TimeZone} timeZone
   * @returns {TimeZone}
   */
  static from (timeZone = 'UTC') {
    if (timeZone instanceof TimeZone) {
      return timeZone
    }

    if (typeof timeZone !== 'string') {
      timeZone = 'UTC'
    }

    return new TimeZone(timeZone)
  }

  constructor (id) {
    const identifier = TimeZone.getCanonicalIdentifier(id)
    SlotMap.setInternal(this, 'id', identifier)
  }

  get id () {
    return SlotMap.getInternal(this, 'id')
  }

  fieldsFromEpoch (epochNanoseconds) {
    return Formatter.from(this.id).fieldsFromEpoch(epochNanoseconds)
  }

  /**
   *
   * @param {DateTime} date
   * @param {{ disambiguation: 'compatible' | 'reject' | 'later' }} options
   * @returns {bigint}
   */
  getEpochNanoseconds (date, options) {
    options = options === undefined ? {} : options

    const disambiguation = getOption(
      options,
      'disambiguation',
      'string',
      ['compatible', 'reject', 'later'],
      'compatible'
    )

    const cache = ObjectCache.from(date)
    let epochNanoseconds = cache.get('epochNanoseconds|' + disambiguation)

    if (epochNanoseconds === undefined) {
      epochNanoseconds = disambiguatePossibleInstants(
        this,
        this.getPossibleEpochNanosecondsFor(date, disambiguation)
      )
      cache.set('epochNanoseconds', epochNanoseconds)
    }

    return epochNanoseconds
  }

  getPossibleEpochNanosecondsFor (date) {
    const slotMap = SlotMap.get(date)
    const ns = epochFromParts(
      slotMap.get('year'),
      slotMap.get('month'),
      slotMap.get('day'),
      slotMap.get('hour'),
      slotMap.get('minute'),
      slotMap.get('second'),
      slotMap.get('millisecond'),
      slotMap.get('microsecond'),
      slotMap.get('nanosecond')
    )

    if (ns === null) throw new RangeError('DateTime outside of supported range')
    let nsEarlier = ns - DAY_NANOS
    if (nsEarlier < NS_MIN) nsEarlier = ns
    let nsLater = ns + DAY_NANOS
    if (nsLater > NS_MAX) nsLater = ns
    const formatter = Formatter.from(this.id)
    const earliest = formatter.toOffsetNanoseconds(nsEarlier)
    const latest = formatter.toOffsetNanoseconds(nsLater)
    const found = earliest === latest ? [earliest] : [earliest, latest]
    return found
      .map((offsetNanoseconds) => {
        const epochNanoseconds = ns - BigInt(offsetNanoseconds)
        const parts = formatter.fieldsFromEpoch(epochNanoseconds)
        if (
          slotMap.get('year') !== parts.year ||
          slotMap.get('month') !== parts.month ||
          slotMap.get('day') !== parts.day ||
          slotMap.get('hour') !== parts.hour ||
          slotMap.get('minute') !== parts.minute ||
          slotMap.get('second') !== parts.second ||
          slotMap.get('millisecond') !== parts.millisecond ||
          slotMap.get('microsecond') !== parts.microsecond ||
          slotMap.get('nanosecond') !== parts.nanosecond
        ) {
          return undefined
        }
        return epochNanoseconds
      })
      .filter((x) => x !== undefined)
  }

  getOffsetStringFor (date) {
    const offsetNanosecondsParam = this.getOffsetNanosecondsFor(date)
    const sign = offsetNanosecondsParam < 0 ? '-' : '+'
    const offsetNanoseconds = Math.abs(offsetNanosecondsParam)
    const nanoseconds = offsetNanoseconds % 1e9
    const seconds = Math.floor(offsetNanoseconds / 1e9) % 60
    const minutes = Math.floor(offsetNanoseconds / 60e9) % 60
    const hours = Math.floor(offsetNanoseconds / 3600e9)

    const hourString = `00${hours}`.slice(-2)
    const minuteString = `00${minutes}`.slice(-2)
    const secondString = `00${seconds}`.slice(-2)
    let post = ''

    if (nanoseconds) {
      let fraction = `${nanoseconds}`.padStart(9, '0')
      while (fraction[fraction.length - 1] === '0') fraction = fraction.slice(0, -1)
      post = `:${secondString}.${fraction}`
    } else if (seconds) {
      post = `:${secondString}`
    }

    return `${sign}${hourString}:${minuteString}${post}`
  }

  /**
   *
   * @param {DateTime} date
   * @returns {number}
   */
  getOffsetNanosecondsFor (date) {
    if (!date.isZonedDate) {
      return ZERO
    }

    const cache = ObjectCache.from(date)
    let offsetNanoseconds = cache.get('offsetNanoseconds')

    if (offsetNanoseconds === undefined) {
      const slotMap = SlotMap.get(date)

      const ns = epochFromParts(
        slotMap.get('year'),
        slotMap.get('month'),
        slotMap.get('day'),
        slotMap.get('hour'),
        slotMap.get('minute'),
        slotMap.get('second'),
        slotMap.get('millisecond'),
        slotMap.get('microsecond'),
        slotMap.get('nanosecond')
      )

      if (ns === null) throw new RangeError('DateTime outside of supported range')
      let nsEarlier = ns - DAY_NANOS
      if (nsEarlier < NS_MIN) nsEarlier = ns
      let nsLater = ns + DAY_NANOS
      if (nsLater > NS_MAX) nsLater = ns
      const formatter = Formatter.from(this.id)
      const earliest = formatter.toOffsetNanoseconds(nsEarlier)
      const latest = formatter.toOffsetNanoseconds(nsLater)

      if (earliest === latest) {
        offsetNanoseconds = earliest
      } else {
        let parts = formatter.fieldsFromEpoch(ns - BigInt(earliest))
        if (
          slotMap.get('year') === parts.year &&
          slotMap.get('month') === parts.month &&
          slotMap.get('day') === parts.day &&
          slotMap.get('hour') === parts.hour &&
          slotMap.get('minute') === parts.minute &&
          slotMap.get('second') === parts.second &&
          slotMap.get('millisecond') === parts.millisecond &&
          slotMap.get('microsecond') === parts.microsecond &&
          slotMap.get('nanosecond') === parts.nanosecond
        ) {
          offsetNanoseconds = earliest
        } else if (
          (parts = formatter.fieldsFromEpoch(ns - BigInt(latest))) &&
          slotMap.get('year') === parts.year &&
          slotMap.get('month') === parts.month &&
          slotMap.get('day') === parts.day &&
          slotMap.get('hour') === parts.hour &&
          slotMap.get('minute') === parts.minute &&
          slotMap.get('second') === parts.second &&
          slotMap.get('millisecond') === parts.millisecond &&
          slotMap.get('microsecond') === parts.microsecond &&
          slotMap.get('nanosecond') === parts.nanosecond
        ) {
          offsetNanoseconds = latest
        } else {
          offsetNanoseconds = Number((BigInt(earliest) + BigInt(latest)) / BigInt(2))
        }
      }

      cache.set('offsetNanoseconds', offsetNanoseconds)
    }

    return offsetNanoseconds
  }

  toString () {
    return this.id
  }

  toJSON () {
    return this.toString()
  }

  toBSON () {
    return this.toString()
  }
}

function epochFromParts (
  year,
  month,
  day,
  hour,
  minute,
  second,
  millisecond,
  microsecond,
  nanosecond
) {
  // Note: Date.UTC() interprets one and two-digit years as being in the
  // 20th century, so don't use it
  const legacyDate = new Date()
  legacyDate.setUTCHours(hour, minute, second, millisecond)
  legacyDate.setUTCFullYear(year, month - 1, day)
  const ms = legacyDate.getTime()
  if (Number.isNaN(ms)) return null
  let ns = BigInt(ms) * MILLION
  ns += (BigInt(microsecond) * THOUSAND)
  ns += BigInt(nanosecond)
  if (ns < NS_MIN || ns > NS_MAX) return null
  return ns
}
